import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import withRoleGuard from '../hoc/withRoleGuard';
import Loader from '../components/Loader';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUser, FaSearch, FaPhone, FaIdCard, FaCalendarAlt, FaClock, FaTimes, FaHashtag, FaDatabase, FaUserMd, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const ITEMS_PER_PAGE = 12;

const PatientCard = () => {
  const { user, token } = useAuth();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loadingAppointments, setLoadingAppointments] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    const fetchPatients = async () => {
      if (!user || !token) {
        setError('Please log in to view patients.');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const config = { headers: { Authorization: `Bearer ${token}` } };
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/patients`,
          config
        );

        let patientsArray = Array.isArray(response.data) 
          ? response.data 
          : response.data?.data || [];

        const validPatients = patientsArray.filter(patient =>
          patient && typeof patient === 'object' && patient.nationalId && patient.fullName
        );
        setPatients(validPatients);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load patients');
      } finally {
        setLoading(false);
      }
    };

    fetchPatients();
  }, [user, token]);

  const fetchPatientAppointments = async (patientId, patientDocId) => {
    setLoadingAppointments(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      // Try fetching by patient nationalId
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/appointments/patient/${patientId}`,
        config
      );
      let appts = Array.isArray(response.data) ? response.data : response.data?.data || [];
      
      // If no appointments found, also try fetching all student appointments and filter
      if (appts.length === 0) {
        try {
          const myAppts = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/appointments/my-appointments`,
            config
          );
          const allAppts = Array.isArray(myAppts.data) ? myAppts.data : myAppts.data?.data || [];
          appts = allAppts.filter(apt => 
            apt.nationalId === patientId || 
            apt.patient === patientId ||
            apt.patient === patientDocId
          );
        } catch (e) {
          console.log('Could not fetch my-appointments:', e);
        }
      }
      
      setAppointments(appts);
    } catch (err) {
      console.error('Failed to fetch appointments:', err);
      setAppointments([]);
    } finally {
      setLoadingAppointments(false);
    }
  };

  const handlePatientClick = (patient) => {
    setSelectedPatient(patient);
    fetchPatientAppointments(patient.nationalId, patient.id);
  };

  const filteredPatients = patients.filter(patient => {
    const fullName = patient.fullName || '';
    const nationalId = patient.nationalId || '';
    const phoneNumber = patient.phoneNumber || '';
    const serialNumber = patient.serialNumber || '';
    const search = searchTerm.toLowerCase();
    return (
      fullName.toLowerCase().includes(search) ||
      nationalId.toLowerCase().includes(search) ||
      phoneNumber.includes(searchTerm) ||
      serialNumber.toLowerCase().includes(search)
    );
  });

  // Pagination
  const totalPages = Math.ceil(filteredPatients.length / ITEMS_PER_PAGE);
  const paginatedPatients = filteredPatients.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'cancelled': return 'bg-red-100 text-red-700 border-red-200';
      case 'pending': return 'bg-amber-100 text-amber-700 border-amber-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    
    // Handle Firestore timestamp objects
    let date;
    if (dateString._seconds) {
      date = new Date(dateString._seconds * 1000);
    } else if (dateString.seconds) {
      date = new Date(dateString.seconds * 1000);
    } else if (typeof dateString === 'object' && dateString.toDate) {
      date = dateString.toDate();
    } else {
      date = new Date(dateString);
    }
    
    if (isNaN(date.getTime())) return 'N/A';
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString;
  };

  if (loading) return <Loader />;

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-cyan-50">
      <Sidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="max-w-7xl mx-auto">
            <motion.div initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="mb-8">
                <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#0077B6] to-[#00B4D8] bg-clip-text text-transparent mb-2">
                  Patient Cards
                </h1>
                <p className="text-gray-600">View patient identification cards and appointment history</p>
              </div>

              {/* Search */}
              <div className="relative mb-6 max-w-md">
                <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, ID, or phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-3 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-[#0077B6] focus:border-transparent transition-all"
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl mb-6">
                  {error}
                </div>
              )}

              {/* Patient Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {paginatedPatients.map((patient) => (
                  <motion.div
                    key={patient.nationalId}
                    whileHover={{ scale: 1.02, y: -4 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => handlePatientClick(patient)}
                    className="bg-white rounded-2xl p-5 shadow-md hover:shadow-xl cursor-pointer border border-gray-100 transition-all duration-300"
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#0077B6] to-[#00B4D8] flex items-center justify-center text-white text-xl font-bold shadow-lg">
                        {patient.fullName?.charAt(0)?.toUpperCase() || 'P'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">{patient.fullName}</h3>
                        <p className="text-sm text-gray-500 truncate">{patient.nationalId}</p>
                      </div>
                    </div>
                    <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <FaPhone className="text-[#0077B6]" />
                        {patient.phoneNumber || 'N/A'}
                      </span>
                      <span className="text-xs bg-[#0077B6]/10 text-[#0077B6] px-2 py-1 rounded-full">
                        View Card
                      </span>
                    </div>
                  </motion.div>
                ))}
              </div>

              {filteredPatients.length === 0 && !loading && (
                <div className="text-center py-16">
                  <FaUser className="mx-auto text-6xl text-gray-300 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600">No patients found</h3>
                  <p className="text-gray-400 mt-2">Try adjusting your search criteria</p>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2 mt-8">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-2 rounded-lg bg-white border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <FaChevronLeft className="text-gray-600" />
                  </button>
                  
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-10 h-10 rounded-lg font-medium transition-all ${
                            currentPage === pageNum
                              ? 'bg-[#0077B6] text-white shadow-md'
                              : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-2 rounded-lg bg-white border border-gray-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                  >
                    <FaChevronRight className="text-gray-600" />
                  </button>
                  
                  <span className="ml-4 text-sm text-gray-500">
                    {filteredPatients.length} patients
                  </span>
                </div>
              )}
            </motion.div>
          </div>
        </main>
      </div>

      {/* Patient Card Modal */}
      <AnimatePresence>
        {selectedPatient && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setSelectedPatient(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gradient-to-br from-white to-blue-50 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl"
            >
              {/* Card Header - ID Card Style */}
              <div className="bg-gradient-to-r from-[#0077B6] via-[#0096C7] to-[#00B4D8] p-6 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />
                
                <div className="relative flex items-start justify-between">
                  <div className="flex items-center gap-5">
                    <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center text-white text-3xl font-bold shadow-lg border-2 border-white/30">
                      {selectedPatient.fullName?.charAt(0)?.toUpperCase() || 'P'}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-white mb-1">{selectedPatient.fullName}</h2>
                      <p className="text-white/80 text-sm">Patient Identification Card</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedPatient(null)}
                    className="text-white/80 hover:text-white p-2 hover:bg-white/20 rounded-full transition-all"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>
              </div>

              {/* Card Body */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
                {/* Patient Info Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                      <FaHashtag /> Serial Number
                    </div>
                    <p className="font-semibold text-gray-900">{selectedPatient.serialNumber || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                      <FaDatabase /> DB Serial
                    </div>
                    <p className="font-semibold text-gray-900">{selectedPatient.dbSerialNumber || selectedPatient.id || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                      <FaIdCard /> National ID
                    </div>
                    <p className="font-semibold text-gray-900">{selectedPatient.nationalId}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                      <FaPhone /> Phone
                    </div>
                    <p className="font-semibold text-gray-900">{selectedPatient.phoneNumber || 'N/A'}</p>
                  </div>
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 col-span-2">
                    <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
                      <FaUserMd /> Assigned Student
                    </div>
                    <p className="font-semibold text-gray-900">{user?.name || 'N/A'}</p>
                    <p className="text-sm text-gray-500">ID: {user?.studentId || user?.id || 'N/A'}</p>
                  </div>
                </div>

                {/* Appointments Section */}
                <div className="mt-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <FaCalendarAlt className="text-[#0077B6]" />
                    Appointment History
                  </h3>

                  {loadingAppointments ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0077B6]"></div>
                    </div>
                  ) : appointments.length > 0 ? (
                    <div className="space-y-3">
                      {appointments.map((apt, index) => (
                        <motion.div
                          key={apt.id || index}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: index * 0.05 }}
                          className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-center justify-between flex-wrap gap-2">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg bg-[#0077B6]/10 flex items-center justify-center">
                                <FaCalendarAlt className="text-[#0077B6]" />
                              </div>
                              <div>
                                <p className="font-medium text-gray-900">{apt.type || apt.procedureType || 'Appointment'}</p>
                                <div className="flex items-center gap-3 text-sm text-gray-500">
                                  <span>{formatDate(apt.date || apt.appointmentDate)}</span>
                                  <span className="flex items-center gap-1">
                                    <FaClock className="text-xs" />
                                    {formatTime(apt.time || apt.appointmentTime)}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(apt.status)}`}>
                              {apt.status || 'Pending'}
                            </span>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-xl">
                      <FaCalendarAlt className="mx-auto text-4xl text-gray-300 mb-2" />
                      <p className="text-gray-500">No appointments found</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default withRoleGuard(['student'])(PatientCard);

